import Head from 'next/head'
import Hero from 'components/folds/locaties-hero'
import Button from 'atoms/button'
import { Container, Row, Col } from 'react-bootstrap'
import Image from 'atoms/image';
import { useState } from 'react';
import useGoogleLocation from 'swr-hooks/use-google-location';
import PartnerSearch from 'components/folds/partner-search';
import PartnerMap from 'components/folds/partner-map'
import { fetchAllPostsOfType } from 'lib/wp-fetcher'
import distance from 'lib/calculate-distance'
import CtaFold from 'components/folds/common/cta-fold'
import CallButton from 'molecules/call-button'
import EmailModalButton from 'molecules/email-modal-button'
import { fetchGeneralCtas } from 'lib/cta-fetcher';
import ReviewsFold from 'components/folds/common/reviews-fold'
import { fetchRecentReviews, fetchReviewsSummary } from 'lib/feedbackcompany-fetcher';

const mapsCenter = {
  lat: 52.1326,
  lng: 5.2913
};

export default function LocationFinder({
  locations: unsortedLocations,
  specialisms,
  generalCtaPosts,
  reviewData,
}) {
  const [focusedLocation, setFocusedLocation] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [maxResults, setMaxResults] = useState(50);
  const [specialismId, setSpecialismId] = useState('all');

  const { data: coordinates, isValidating } = useGoogleLocation(searchTerm, {
    initialData: mapsCenter
  });

  const locations = unsortedLocations
    .filter(({ acf }) => specialismId === 'all' || acf?.offered_specialisms.includes(parseInt(specialismId)))
    .map(location => ({
      ...location,
      distance: distance(location.acf.main_address, coordinates)
    }))
    .sort((a, b) => {
      if(! searchTerm) {
        const aIsFranchise = a.acf?.location_type === 'franchise'
        const bIsFranchise = b.acf?.location_type === 'franchise'

        if (aIsFranchise !== bIsFranchise) {
          return aIsFranchise 
            ? -1 
            : 1
        }

        return a.title.rendered.localeCompare(b.title.rendered)
      }

      return a.distance - b.distance
    })
    .slice(0, maxResults)


  return (
    <div>
      <Head>
        <title>Fysiotherapie4all - Locaties</title>
        <script type="text/javascript">{`if (typeof acalltracker !== 'undefined'){acalltracker.dynamicPageload();}`}</script>
      </Head>
      <Hero />
      <Container>
        <h2 className="text-center mb-1 ">Zoek hier een praktijk bij jou in de buurt</h2>
        <a id="zoek-een-locatie" className="anchor-100-px" />
        <Row className="justify-content-center">
          <Col className="col-12 col-lg-6 mb-5 order-lg-3">
            <PartnerMap
              locations={locations}
              center={mapsCenter}
              focusedLocation={focusedLocation}
              sticky
            />
          </Col>
          <Col className="col-12">
            <PartnerSearch
              onSearchSubmitted={setSearchTerm}
              onMaxResultsChanged={setMaxResults}
              onSpecialismChanged={setSpecialismId}
              specialisms={specialisms}
            />
          </Col>
          <Col className="col-12 col-lg-6 order-2 order-lg-1">
            {locations.map(location => {
              return (
                <div
                  className="location-row"
                  key={`location-item-${location.id}`}
                  onMouseEnter={() => setFocusedLocation(location.id)}
                  onMouseLeave={() => setFocusedLocation(0)}
                >
                  <Row className="border-bottom pt-4 mx-0 flex-fill">
                  <Col className="col-7">
                      <p className="text-bold">{location.title.rendered}</p>
                      <p>
                        {location.acf?.main_address?.street_name} {location.acf?.main_address?.street_number}<br />
                        {`${location.acf?.main_address?.post_code} ${location.acf?.main_address?.city}`}
                      </p>
                      <p className="location-contact">
                        {location.acf?.phone_number && <CallButton 
                          label="Bel"
                          phoneNumber={location.acf.phone_number}
                          gtmIdentifier={location.slug}
                          showPhoneNumber={location.acf.show_phone_number?.length}
                        />}
                        {' '}
                        {location.acf?.email_address && <EmailModalButton
                          label="E-mail"
                          sendToEmail={location.acf.email_address}
                          gtmIdentifier={location.slug}
                          {...(location.acf?.custom_welcome_url && location.acf?.custom_welcome_url.length ? {successUrl:location.acf.custom_welcome_url} : {})}
                        />}
                        {location.acf?.appointment_url && <Button
                            target="_blank"
                            link={location.acf.appointment_url}
                          >
                            Plan afspraak
                          </Button>
                        }
                      </p>
                      <p>
                        <a className="btn btn-secondary" href={`/locatie/${location.slug}`}>Bekijk praktijk</a>
                        {' '}
                        {location.website && <Button link={location.website}>Website</Button>}
                      </p>
                    </Col>
                    
                    <Col>
                        { location.acf?.branch_image && <Image src={location.acf?.branch_image} /> }
                        { searchTerm && <div className="text-center">{ location.distance.toFixed(0) } km</div> }
                    </Col>
                  </Row>
                </div>
              )
            })}
          </Col>
        </Row>
      </Container>

      <CtaFold ctaPosts={generalCtaPosts} className="mb-5" />

      <ReviewsFold
        background={false}
        compensateForBackground={false}
        reviews={reviewData.reviews}
        summary={reviewData.summary}
      />

      <style jsx>{`
        .location-row:hover {
          background-color: #FBFBFB;
        }

        .email-btn {
          position: relative;
          padding-left: 20px;
        }

        .email-btn:before {
          position: absolute;
          top: 0;
          left: 0;
          content: " ";
          width: 12px;
          height: 12px;
          background: url('/img/icons/envelope-white.svg')
        }
      `}</style>
    </div>
  )
}

export async function getStaticProps() {
  const allSpecialisms = await fetchAllPostsOfType('specialisms')
  const allLocations = await fetchAllPostsOfType('partners')
  const locations = allLocations
    .filter(location => location.acf?.main_address);

  return {
    props: {
      locations,
      specialisms: allSpecialisms,
      generalCtaPosts: await fetchGeneralCtas(),
      reviewData: {
        reviews: await fetchRecentReviews(),
        summary: await fetchReviewsSummary(),
      }
    },
    revalidate: parseInt(process.env.REVALIDATION_TIME_SECONDS),
  }
}
